import Decimal from "decimal.js";
// NOTE: 산출내역서 행 조작 관련 유탈 험수 모음
/**
 * @description 빈 행 추가
 * @param costList 행의 이름, 없으면 빈 문자열로 설정
 * @param colSpan 행의 열 개수, 없으면 4로 설정
 */
export const addEmptyRow = (costList, colSpan) => {
    return {
        cost_list: costList ?? "",
        cost_list_colspan: colSpan ?? 4,
        cost_unit: "",
        cost_num: null,
        cost_unit_price: null,
        cost_total: null,
        belong_data: [],
    };
};
/**
 * @description 특정 항목 이름을 가진 행 찾기
 * @param items 행 목록
 * @param costList 찾을 행의 이름
 */
export const findRow = (items, costList) => {
    return items.find((row) => row.cost_list === costList);
};
/**
 * @description 특정 데이터 행 설정
 * @param targetRow 설정할 부모 행
 * @param costList 설정할 행의 이름
 * @param data 설정할 데이터
 */
export const setCalcCostRow = (targetRow, costList, data) => {
    if (!data || data.length === 0) {
        // 이미 '재료'·'공사 자재' 같은 행이 있으면 제거
        const idx = targetRow.belong_data?.findIndex((row) => row.cost_list === costList);
        if (idx !== undefined && idx > -1) {
            targetRow.belong_data.splice(idx, 1);
        }
        if (!targetRow.belong_data || targetRow.belong_data.length === 0) {
            targetRow.belong_data = [];
        }
        return;
    }
    let newRow = findRow(targetRow.belong_data ?? [], costList);
    if (!newRow) {
        newRow = addEmptyRow(costList);
        (targetRow.belong_data ??= []).push(newRow);
    }
    newRow.belong_data = data;
};
/**
 * @description 빈 자식 행(= cost_list_colspan이 5인 행) 판별
 * @param row 판별할 행
 */
export const validateEmptyChildRow = (row) => row.belong_data?.[0]?.cost_list_colspan === 5;
/**
 * @description 한 행의 cost_total 계산 → 정수 반올림
 * @param row 계산할 행
 */
export const updateRowTotals = (row) => {
    if (row.belong_data?.length && !validateEmptyChildRow(row)) {
        const sum = row.belong_data.reduce((acc, c) => acc + updateRowTotals(c), 0);
        row.cost_total = sum;
    }
    else {
        const raw = (row.cost_num ?? 0) * (row.cost_unit_price ?? 0);
        row.cost_total = Math.round(raw);
    }
    return row.cost_total;
};
/**
 * @description 전체 행 합계 갱신
 * @param rows 갱신할 행 목록
 */
export const updateAllTotals = (rows) => {
    rows.forEach(updateRowTotals);
};
/**
 * @description 수식에 따른 단가 설정 → 정수 반올림
 * @param targetRow 설정할 부모 행
 * @param outputRatios 산출비율 데이터
 * @param getBaseValue 기준 값 가져오기 함수
 */
export const setUnitPricesByFormula = (targetRow, outputRatios, getBaseValue) => {
    if (!targetRow.belong_data)
        return;
    const applyUnitPrice = (row) => {
        const f = outputRatios.find((x) => x.type === row.cost_list);
        const base = getBaseValue(row.cost_list);
        // base * ratio → 정수로 반올림
        row.cost_unit_price = new Decimal(base)
            .times(f?.ratio ?? 0)
            .toDecimalPlaces(0, Decimal.ROUND_HALF_UP) // 0자리 = 정수, 반올림
            .toNumber();
        updateRowTotals(row);
    };
    if (validateEmptyChildRow(targetRow)) {
        applyUnitPrice(targetRow);
    }
    else {
        targetRow.belong_data.forEach(applyUnitPrice);
    }
};
export const parseCalcJson = (json) => {
    // 1) 빠른 종료: 배열이면 그대로
    if (Array.isArray(json)) {
        if (json.length === 0)
            return [addEmptyRow()];
        return json;
    }
    // 2) 객체일 수 있는 경우: 진짜 배열인지 확인
    if (json && typeof json === "object") {
        // 만약 서버가 { productCalcJson: [...] } 형태로 줬다면
        const maybe = "productCalcJson" in json
            ? json.productCalcJson
            : undefined;
        if (Array.isArray(maybe)) {
            if (maybe.length === 0)
                return [addEmptyRow()];
            return maybe;
        }
        // 객체 자체를 배열로 착각하지 않도록
        return [addEmptyRow()];
    }
    // 3) 문자열인 경우: 정상 파싱 → 실패하면 과이스케이프 보정 후 재시도
    if (typeof json === "string") {
        const s = json.trim();
        try {
            return JSON.parse(s);
        }
        catch (e1) {
            try {
                // 흔한 케이스: \\" 가 들어있는 과이스케이프 문자열
                const fixed = s
                    .replace(/\\"/g, '"')
                    .replace(/\\n/g, "\n")
                    .replace(/\\t/g, "\t");
                return JSON.parse(fixed);
            }
            catch (e2) {
                console.error("productCalcJson parse failed", { s, e1, e2 });
                return [addEmptyRow()];
            }
        }
    }
    // 4) 그 외
    return [addEmptyRow()];
};
export const parseTotalPrice = (json) => {
    const rows = parseCalcJson(json);
    return rows.reduce((acc, curr) => {
        if (curr.hidden)
            return acc;
        return Number(acc) + Number(curr.cost_total);
    }, 0);
};
